import { useState, useCallback, useMemo, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import type { User } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import { useFeatures } from '../../hooks/use-features'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ConfirmDialog,
  Modal,
  FormField,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  btnDanger,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { roleSatisfies } from '@alpha-edge/shared'
import { useTableColumns } from '../../hooks/use-table-columns'

interface FleetCustomer {
  readonly id: string
  readonly name: string
}

const btnSmall: CSSProperties = {
  padding: '0.2rem 0.55rem',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}

const btnSmallPrimary: CSSProperties = { ...btnSmall, background: colors.primary, color: '#fff' }
const btnSmallDanger: CSSProperties = { ...btnSmall, background: colors.danger, color: '#fff' }

const errorBox: CSSProperties = {
  marginTop: '0.75rem',
  color: colors.danger,
  padding: '0.5rem 0.75rem',
  background: colors.bgError,
  border: `1px solid ${colors.borderError}`,
  borderRadius: '3px',
  fontSize: '0.875rem',
}

const baseColumns: readonly Column<User>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (u) => (
      <strong>
        {u.firstName} {u.lastName}
      </strong>
    ),
  },
  { key: 'email', header: 'Email', render: (u) => u.email },
  { key: 'role', header: 'Role', render: (u) => <StatusBadge status={u.role} /> },
  { key: 'status', header: 'Status', render: (u) => <StatusBadge status={u.status} /> },
  {
    key: 'lastLogin',
    header: 'Last Login',
    render: (u) => (u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'),
  },
]

export function UserListPage() {
  const navigate = useNavigate()
  const { user: currentUser, isImpersonating, startImpersonation } = useAuth()
  const features = useFeatures()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<User>({
    path: '/users',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('users')

  // Promote state
  const [promoteTarget, setPromoteTarget] = useState<User | null>(null)
  const [promoting, setPromoting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Demote state
  const [demoteTarget, setDemoteTarget] = useState<User | null>(null)
  const [demoteOpen, setDemoteOpen] = useState(false)
  const [demoteOrgs, setDemoteOrgs] = useState<readonly FleetCustomer[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [demoting, setDemoting] = useState(false)
  const [demoteError, setDemoteError] = useState<string | null>(null)

  const isMedflowAdmin = currentUser?.role === 'medflow_admin'
  const isAdmin = currentUser?.role ? roleSatisfies(currentUser.role, ['admin']) : false
  const canImpersonate = isAdmin && !isImpersonating
  const showActions = (isMedflowAdmin && features.central) || canImpersonate

  const handlePromoteClick = useCallback((u: User, e: React.MouseEvent) => {
    e.stopPropagation()
    setActionError(null)
    setPromoteTarget(u)
  }, [])

  const handlePromoteConfirm = useCallback(async () => {
    if (!promoteTarget) return
    setPromoting(true)
    setActionError(null)
    try {
      await api.post(`/users/${promoteTarget.id}/promote-medflow-admin`, {})
      refresh()
      setPromoteTarget(null)
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to promote user')
      setPromoteTarget(null)
    } finally {
      setPromoting(false)
    }
  }, [promoteTarget, refresh])

  const handleDemoteClick = useCallback(async (u: User, e: React.MouseEvent) => {
    e.stopPropagation()
    setActionError(null)
    setDemoteError(null)
    setSelectedOrgId('')
    setDemoteTarget(u)
    try {
      const res = await api.get<readonly FleetCustomer[]>('/fleet/customers')
      setDemoteOrgs(res.data ?? [])
    } catch {
      setDemoteOrgs([])
    }
    setDemoteOpen(true)
  }, [])

  const handleDemoteConfirm = useCallback(async () => {
    if (!demoteTarget || !selectedOrgId) return
    setDemoting(true)
    setDemoteError(null)
    try {
      await api.post(`/users/${demoteTarget.id}/demote-medflow-admin`, { organizationId: selectedOrgId })
      refresh()
      setDemoteOpen(false)
      setDemoteTarget(null)
    } catch (err) {
      setDemoteError(err instanceof ApiError ? err.message : 'Failed to demote user')
    } finally {
      setDemoting(false)
    }
  }, [demoteTarget, selectedOrgId, refresh])

  const handleDemoteClose = useCallback(() => {
    setDemoteOpen(false)
    setDemoteTarget(null)
    setDemoteError(null)
  }, [])

  const columns = useMemo((): readonly Column<User>[] => {
    if (!showActions) return baseColumns
    const actionsColumn: Column<User> = {
      key: 'actions',
      header: 'Actions',
      render: (u) => {
        if (u.id === currentUser?.id) return null
        const buttons: React.ReactNode[] = []
        if (isMedflowAdmin && features.central) {
          if (u.role !== 'medflow_admin') {
            buttons.push(
              <button
                key="promote"
                type="button"
                style={btnSmallPrimary}
                onClick={(e) => handlePromoteClick(u, e)}
                title={`Promote ${u.email} to MedFlow admin`}
              >
                Promote
              </button>,
            )
          } else {
            buttons.push(
              <button
                key="demote"
                type="button"
                style={btnSmallDanger}
                onClick={(e) => handleDemoteClick(u, e)}
                title={`Demote ${u.email} from MedFlow admin`}
              >
                Demote
              </button>,
            )
          }
        }
        if (canImpersonate && u.role !== 'medflow_admin') {
          buttons.push(
            <button
              key="impersonate"
              type="button"
              style={{ ...btnSmall, background: '#6366f1', color: '#fff' }}
              onClick={(e) => {
                e.stopPropagation()
                startImpersonation(u.id).catch(() => {})
              }}
              title={`Impersonate ${u.email}`}
            >
              Impersonate
            </button>,
          )
        }
        return buttons.length > 0 ? <div style={{ display: 'flex', gap: '0.35rem' }}>{buttons}</div> : null
      },
    }
    return [...baseColumns, actionsColumn]
  }, [
    showActions,
    currentUser,
    isMedflowAdmin,
    features.central,
    canImpersonate,
    handlePromoteClick,
    handleDemoteClick,
    startImpersonation,
  ])

  const selectedOrg = demoteOrgs.find((o) => o.id === selectedOrgId)

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/settings/users/new')} style={btnPrimary}>
              Add User
            </button>
          </>
        }
      />
      {actionError && (
        <div
          style={{
            ...errorBox,
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              color: colors.danger,
              marginLeft: '1rem',
            }}
          >
            x
          </button>
        </div>
      )}
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(u) => navigate(`/users/${u.id}`)}
            emptyMessage="No users found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      {/* Promote confirmation dialog */}
      <ConfirmDialog
        isOpen={promoteTarget !== null}
        onClose={() => setPromoteTarget(null)}
        onConfirm={handlePromoteConfirm}
        title="Promote to MedFlow Admin"
        message={`Promote ${promoteTarget?.email} to MedFlow admin? This removes their organization scope and grants cross-org access.`}
        confirmLabel="Promote"
        variant="primary"
        loading={promoting}
      />

      {/* Demote modal -- requires selecting the destination organization */}
      <Modal isOpen={demoteOpen} onClose={handleDemoteClose} title="Demote from MedFlow Admin" width="480px">
        <p style={{ marginBottom: '1rem', fontSize: '0.9375rem' }}>
          Demote <strong>{demoteTarget?.email}</strong> from MedFlow admin? Select the organization they will be
          re-scoped to as a customer_admin.
        </p>
        <FormField name="demoteOrg" label="Re-scope to organization" required>
          <select
            style={input}
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={demoteOrgs.length === 0}
          >
            <option value="">-- select organization --</option>
            {demoteOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>
        {demoteError && <div style={errorBox}>{demoteError}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button type="button" style={btnSecondary} onClick={handleDemoteClose} disabled={demoting}>
            Cancel
          </button>
          <button
            type="button"
            style={{ ...btnDanger, opacity: !selectedOrgId || demoting ? 0.5 : 1 }}
            onClick={handleDemoteConfirm}
            disabled={!selectedOrgId || demoting}
          >
            {demoting ? 'Demoting...' : `Demote${selectedOrg ? ` to ${selectedOrg.name}` : ''}`}
          </button>
        </div>
      </Modal>
    </div>
  )
}
